import React, { useState, useEffect, useCallback } from 'react';
import { Technique } from '../types';

// API base URL (same as MagicLogoModal)
const API_BASE_URL = '';

interface LogoAnalysis {
  format: string;
  width: number;
  height: number;
  dpi: number;
  dpiWarning: boolean;
  colors: string[];
  colorCount: number;
  hasTransparentBackground: boolean;
  hasWhiteBackground: boolean;
  dominantColors: { hex: string; count: number; percentage: number }[];
}

interface LogoWizardProps {
  logoBase64: string;
  originalBase64?: string; // Pre-vectorization raster image for accurate Vision color analysis
  techniques: Technique[];
  language: 'de' | 'nl' | 'en';
  originalFormat?: string; // Original file format (eps, ai, png, etc.)
  onComplete: (result: {
    logoSrc: string;
    selectedTechnique: string;
    selectedColors: string;
    backgroundRemoved: boolean;
    detectedColors?: string[];
  }) => void;
  onCancel: () => void;
}

type WizardStep = 'quality' | 'colors' | 'background' | 'technique';

// Translations
const translations = {
  de: {
    wizardTitle: 'Wizards Magic',
    quality: 'Qualität',
    colors: 'Farben',
    background: 'Hintergrund',
    vectorize: 'Vektorisieren',
    technique: 'Technik',
    next: 'Weiter',
    previous: 'Zurück',
    finish: 'Fertig',
    cancel: 'Abbrechen',
    restart: 'Neustart',
    qualityTitle: 'Logo Qualität',
    dpi: 'DPI',
    dimensions: 'Abmessungen',
    format: 'Format',
    qualityWarning: 'Die Auflösung ist niedriger als 300 DPI. Dies kann zu einem weniger scharfen Druck führen.',
    qualityOk: 'Die Qualität ist ausreichend für einen scharfen Druck.',
    colorsTitle: 'Erkannte Farben',
    colorsDetected: 'Farben erkannt',
    colorsToggle: 'Klicken Sie auf eine Farbe um sie zu deaktivieren',
    colorsEnabled: 'Aktive Farben',
    backgroundTitle: 'Hintergrund',
    backgroundTransparent: 'Transparenter Hintergrund erkannt.',
    backgroundWhite: 'Weißer Hintergrund erkannt.',
    backgroundSolid: 'Farbiger Hintergrund erkannt.',
    backgroundRemove: 'Hintergrund entfernen',
    backgroundRemoving: 'Wird entfernt...',
    backgroundRemoved: 'Hintergrund entfernt',
    backgroundKeep: 'Behalten',
    vectorizeTitle: 'Vektorisieren',
    vectorizing: 'Wird vektorisiert...',
    vectorizeSuccess: 'Erfolgreich vektorisiert',
    vectorizeSkip: 'Überspringen',
    techniqueTitle: 'Technik wählen',
    techniqueFilter: 'Gefiltert nach Farbanzahl',
    maxColors: 'Max. Farben',
    noTechniques: 'Keine passenden Techniken',
    analyzing: 'Analysiere...',
    restartConfirm: 'Möchten Sie den Wizard von vorne beginnen?',
  },
  nl: {
    wizardTitle: 'Wizards Magic',
    quality: 'Kwaliteit',
    colors: 'Kleuren',
    background: 'Achtergrond',
    vectorize: 'Vectoriseren',
    technique: 'Techniek',
    next: 'Volgende',
    previous: 'Vorige',
    finish: 'Voltooien',
    cancel: 'Annuleren',
    restart: 'Herstarten',
    qualityTitle: 'Logo Kwaliteit',
    dpi: 'DPI',
    dimensions: 'Afmetingen',
    format: 'Formaat',
    qualityWarning: 'De resolutie is lager dan 300 DPI. Dit kan resulteren in een minder scherpe print.',
    qualityOk: 'De kwaliteit is voldoende voor een scherpe print.',
    colorsTitle: 'Gedetecteerde Kleuren',
    colorsDetected: 'kleuren gedetecteerd',
    colorsToggle: 'Klik op een kleur om deze uit te schakelen',
    colorsEnabled: 'Actieve kleuren',
    backgroundTitle: 'Achtergrond',
    backgroundTransparent: 'Transparante achtergrond gedetecteerd.',
    backgroundWhite: 'Witte achtergrond gedetecteerd.',
    backgroundSolid: 'Gekleurde achtergrond gedetecteerd.',
    backgroundRemove: 'Achtergrond verwijderen',
    backgroundRemoving: 'Bezig met verwijderen...',
    backgroundRemoved: 'Achtergrond verwijderd',
    backgroundKeep: 'Behouden',
    vectorizeTitle: 'Vectoriseren',
    vectorizing: 'Bezig met vectoriseren...',
    vectorizeSuccess: 'Succesvol gevectoriseerd',
    vectorizeSkip: 'Overslaan',
    techniqueTitle: 'Kies Techniek',
    techniqueFilter: 'Gefilterd op aantal kleuren',
    maxColors: 'Max. kleuren',
    noTechniques: 'Geen passende technieken',
    analyzing: 'Analyseren...',
    restartConfirm: 'Wilt u de wizard opnieuw starten?',
  },
  en: {
    wizardTitle: 'Wizards Magic',
    quality: 'Quality',
    colors: 'Colors',
    background: 'Background',
    vectorize: 'Vectorize',
    technique: 'Technique',
    next: 'Next',
    previous: 'Previous',
    finish: 'Finish',
    cancel: 'Cancel',
    restart: 'Restart',
    qualityTitle: 'Logo Quality',
    dpi: 'DPI',
    dimensions: 'Dimensions',
    format: 'Format',
    qualityWarning: 'Resolution is below 300 DPI. This may result in a less sharp print.',
    qualityOk: 'Quality is sufficient for a sharp print.',
    colorsTitle: 'Detected Colors',
    colorsDetected: 'colors detected',
    colorsToggle: 'Click on a color to disable it',
    colorsEnabled: 'Active colors',
    backgroundTitle: 'Background',
    backgroundTransparent: 'Transparent background detected.',
    backgroundWhite: 'White background detected.',
    backgroundSolid: 'Colored background detected.',
    backgroundRemove: 'Remove background',
    backgroundRemoving: 'Removing...',
    backgroundRemoved: 'Background removed',
    backgroundKeep: 'Keep',
    vectorizeTitle: 'Vectorize',
    vectorizing: 'Vectorizing...',
    vectorizeSuccess: 'Successfully vectorized',
    vectorizeSkip: 'Skip',
    techniqueTitle: 'Choose Technique',
    techniqueFilter: 'Filtered by color count',
    maxColors: 'Max. colors',
    noTechniques: 'No matching techniques',
    analyzing: 'Analyzing...',
    restartConfirm: 'Do you want to restart the wizard?',
  },
};

// Simple PMS color database (most common colors)
const PMS_COLORS: { hex: string; name: string }[] = [
  // Reds
  { hex: '#ed1c24', name: 'PMS 485 C' },
  { hex: '#da291c', name: 'PMS 485 C' },
  { hex: '#ce181e', name: 'PMS 186 C' },
  { hex: '#bf0d3e', name: 'PMS 199 C' },
  { hex: '#c8102e', name: 'PMS 186 C' },
  { hex: '#e4002b', name: 'PMS 185 C' },
  // Oranges
  { hex: '#ff6600', name: 'PMS 151 C' },
  { hex: '#ff5500', name: 'PMS Orange 021 C' },
  { hex: '#f26522', name: 'PMS 158 C' },
  { hex: '#ff6b00', name: 'PMS 151 C' },
  { hex: '#fe5000', name: 'PMS 1655 C' },
  // Yellows
  { hex: '#ffcc00', name: 'PMS 116 C' },
  { hex: '#ffd100', name: 'PMS 109 C' },
  { hex: '#ffc72c', name: 'PMS 123 C' },
  { hex: '#ffdd00', name: 'PMS 108 C' },
  { hex: '#fce300', name: 'PMS 102 C' },
  // Greens
  { hex: '#00843d', name: 'PMS 348 C' },
  { hex: '#009639', name: 'PMS 355 C' },
  { hex: '#00a651', name: 'PMS 354 C' },
  { hex: '#007a33', name: 'PMS 349 C' },
  { hex: '#78be20', name: 'PMS 368 C' },
  { hex: '#97d700', name: 'PMS 375 C' },
  // Blues
  { hex: '#0033a0', name: 'PMS 286 C' },
  { hex: '#003087', name: 'PMS 287 C' },
  { hex: '#003da5', name: 'PMS 293 C' },
  { hex: '#0057b8', name: 'PMS 300 C' },
  { hex: '#0072ce', name: 'PMS 285 C' },
  { hex: '#00a3e0', name: 'PMS 299 C' },
  { hex: '#00b5e2', name: 'PMS 306 C' },
  { hex: '#009cde', name: 'PMS 2925 C' },
  // Purple/Violet
  { hex: '#702082', name: 'PMS 259 C' },
  { hex: '#8031a7', name: 'PMS 2597 C' },
  { hex: '#662d91', name: 'PMS 267 C' },
  { hex: '#5c068c', name: 'PMS 2602 C' },
  // Pink/Magenta
  { hex: '#d62598', name: 'PMS 232 C' },
  { hex: '#e91e8c', name: 'PMS Rhodamine Red C' },
  { hex: '#ce0058', name: 'PMS 219 C' },
  { hex: '#d62e6e', name: 'PMS 213 C' },
  // Brown
  { hex: '#6c4c3e', name: 'PMS 469 C' },
  { hex: '#4e3524', name: 'PMS 476 C' },
  { hex: '#8b5b29', name: 'PMS 146 C' },
  // Black/Gray
  { hex: '#000000', name: 'PMS Black C' },
  { hex: '#1a1a1a', name: 'PMS Black C' },
  { hex: '#333333', name: 'PMS Black 7 C' },
  { hex: '#4d4d4d', name: 'PMS Cool Gray 11 C' },
  { hex: '#666666', name: 'PMS Cool Gray 9 C' },
  { hex: '#808080', name: 'PMS Cool Gray 7 C' },
  { hex: '#999999', name: 'PMS Cool Gray 5 C' },
  { hex: '#b3b3b3', name: 'PMS Cool Gray 4 C' },
  { hex: '#cccccc', name: 'PMS Cool Gray 3 C' },
  // White
  { hex: '#ffffff', name: 'PMS White' },
];

// Find closest PMS color for a given hex
const findClosestPMS = (hex: string): string => {
  let minDistance = Infinity;
  let closestPMS = '';

  for (const pms of PMS_COLORS) {
    const dist = colorDistance(hex.toLowerCase(), pms.hex.toLowerCase());
    if (dist < minDistance) {
      minDistance = dist;
      closestPMS = pms.name;
    }
  }

  // If distance is too large, show as "Custom"
  if (minDistance > 100) {
    return '';
  }

  return closestPMS;
};

// Calculate color distance (Euclidean in RGB space)
const colorDistance = (hex1: string, hex2: string): number => {
  const r1 = parseInt(hex1.slice(1, 3), 16);
  const g1 = parseInt(hex1.slice(3, 5), 16);
  const b1 = parseInt(hex1.slice(5, 7), 16);
  const r2 = parseInt(hex2.slice(1, 3), 16);
  const g2 = parseInt(hex2.slice(3, 5), 16);
  const b2 = parseInt(hex2.slice(5, 7), 16);
  return Math.sqrt(Math.pow(r2 - r1, 2) + Math.pow(g2 - g1, 2) + Math.pow(b2 - b1, 2));
};

// Merge similar colors together
const mergeSimilarColors = (
  colors: { hex: string; count: number }[],
  threshold: number = 60
): { hex: string; count: number }[] => {
  if (colors.length === 0) return [];

  const merged: { hex: string; count: number }[] = [];

  for (const color of colors) {
    let foundMatch = false;
    for (const existing of merged) {
      if (colorDistance(color.hex, existing.hex) < threshold) {
        existing.count += color.count;
        foundMatch = true;
        break;
      }
    }
    if (!foundMatch) {
      merged.push({ ...color });
    }
  }

  return merged.sort((a, b) => b.count - a.count);
};

const rgbToHex = (r: number, g: number, b: number): string => {
  return '#' + [r, g, b].map(x => {
    const hex = Math.min(255, Math.max(0, x)).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
};

// Extract colors from SVG by parsing fill/stroke attributes
const extractColorsFromSVG = (svgContent: string): { hex: string; count: number }[] => {
  const colorMap: Map<string, number> = new Map();

  // Match hex colors
  const hexMatches = svgContent.match(/#[0-9A-Fa-f]{6}/g) || [];
  hexMatches.forEach(hex => {
    const normalized = hex.toLowerCase();
    colorMap.set(normalized, (colorMap.get(normalized) || 0) + 1);
  });

  // Match rgb() colors
  const rgbMatches = svgContent.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/g) || [];
  rgbMatches.forEach(rgb => {
    const match = rgb.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
    if (match) {
      const hex = rgbToHex(parseInt(match[1]), parseInt(match[2]), parseInt(match[3]));
      colorMap.set(hex, (colorMap.get(hex) || 0) + 1);
    }
  });

  // Named colors mapping
  const namedColors: Record<string, string> = {
    'black': '#000000', 'white': '#ffffff', 'red': '#ff0000',
    'green': '#008000', 'blue': '#0000ff', 'yellow': '#ffff00',
    'orange': '#ffa500', 'purple': '#800080', 'pink': '#ffc0cb',
  };

  Object.entries(namedColors).forEach(([name, hex]) => {
    const regex = new RegExp(`fill\\s*[:=]\\s*["']?${name}["']?|stroke\\s*[:=]\\s*["']?${name}["']?`, 'gi');
    const matches = svgContent.match(regex) || [];
    if (matches.length > 0) {
      colorMap.set(hex, (colorMap.get(hex) || 0) + matches.length);
    }
  });

  return Array.from(colorMap.entries())
    .map(([hex, count]) => ({ hex, count }))
    .sort((a, b) => b.count - a.count);
};

// Client-side color extraction using Canvas API
const extractColorsFromImage = (base64: string): Promise<LogoAnalysis> => {
  return new Promise((resolve) => {
    // Check if this is an SVG
    if (base64.includes('data:image/svg+xml')) {
      try {
        let svgContent = '';
        if (base64.includes('base64,')) {
          svgContent = atob(base64.split('base64,')[1]);
        } else {
          svgContent = decodeURIComponent(base64.split(',')[1] || '');
        }

        const svgColors = extractColorsFromSVG(svgContent);
        const mergedColors = mergeSimilarColors(svgColors, 80);
        const significantColors = mergedColors.slice(0, 8);

        // Get SVG dimensions
        const widthMatch = svgContent.match(/width\s*=\s*["']?(\d+)/);
        const heightMatch = svgContent.match(/height\s*=\s*["']?(\d+)/);
        const width = widthMatch ? parseInt(widthMatch[1]) : 500;
        const height = heightMatch ? parseInt(heightMatch[1]) : 500;

        const totalCount = significantColors.reduce((sum, c) => sum + c.count, 0);
        const dominantColors = significantColors.map(c => ({
          hex: c.hex,
          count: c.count,
          percentage: totalCount > 0 ? (c.count / totalCount) * 100 : 0,
        }));

        console.log(`SVG color analysis: found ${dominantColors.length} distinct colors`);

        resolve({
          format: 'svg',
          width,
          height,
          dpi: 300, // SVGs are resolution independent
          dpiWarning: false,
          colors: dominantColors.map(c => c.hex),
          colorCount: dominantColors.length,
          hasTransparentBackground: true, // SVGs typically have transparent bg
          hasWhiteBackground: false,
          dominantColors,
        });
        return;
      } catch (e) {
        console.error('Error parsing SVG:', e);
      }
    }

    // For raster images, use Canvas
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(getDefaultAnalysis(img.width, img.height));
        return;
      }

      const maxSize = 150;
      const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
      canvas.width = Math.floor(img.width * scale);
      canvas.height = Math.floor(img.height * scale);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      const colorMap: Map<string, number> = new Map();
      let transparentCount = 0;
      let whiteCount = 0;
      let totalPixels = 0;

      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const a = data[i + 3];

        totalPixels++;

        if (a < 128) {
          transparentCount++;
          continue;
        }

        if (r > 245 && g > 245 && b > 245) {
          whiteCount++;
        }

        const hex = rgbToHex(r, g, b);
        colorMap.set(hex, (colorMap.get(hex) || 0) + 1);
      }

      const sortedColors = Array.from(colorMap.entries())
        .map(([hex, count]) => ({ hex, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 100);

      const mergedColors = mergeSimilarColors(sortedColors, 80);

      const nonTransparentPixels = totalPixels - transparentCount;
      const significantColors = mergedColors
        .filter(c => (c.count / nonTransparentPixels) > 0.01)
        .slice(0, 8);

      const dominantColors = significantColors.map(c => ({
        hex: c.hex,
        count: c.count,
        percentage: nonTransparentPixels > 0 ? (c.count / nonTransparentPixels) * 100 : 0,
      }));

      let format = 'unknown';
      if (base64.includes('data:image/png')) format = 'png';
      else if (base64.includes('data:image/jpeg') || base64.includes('data:image/jpg')) format = 'jpeg';
      else if (base64.includes('data:image/gif')) format = 'gif';
      else if (base64.includes('data:image/webp')) format = 'webp';

      const dpi = Math.min(300, Math.max(72, Math.floor(Math.min(img.width, img.height) / 2)));
      const dpiWarning = img.width < 300 || img.height < 300;

      console.log(`Color analysis: found ${dominantColors.length} distinct colors from ${colorMap.size} raw colors`);

      resolve({
        format,
        width: img.width,
        height: img.height,
        dpi,
        dpiWarning,
        colors: dominantColors.map(c => c.hex),
        colorCount: dominantColors.length,
        hasTransparentBackground: transparentCount > totalPixels * 0.1,
        hasWhiteBackground: whiteCount > nonTransparentPixels * 0.3,
        dominantColors,
      });
    };

    img.onerror = () => {
      resolve(getDefaultAnalysis(0, 0));
    };

    img.src = base64;
  });
};

const getDefaultAnalysis = (width: number, height: number): LogoAnalysis => ({
  format: 'unknown',
  width,
  height,
  dpi: 72,
  dpiWarning: true,
  colors: [],
  colorCount: 0,
  hasTransparentBackground: false,
  hasWhiteBackground: false,
  dominantColors: [],
});

// Analyze colors using Claude Vision API (for raster images) with fallback to Canvas analysis
// originalBase64: original raster before auto-vectorization; used for Vision when base64 is already SVG
const analyzeColorsWithVision = async (base64: string, originalBase64?: string): Promise<LogoAnalysis> => {
  // Always run local analysis first for DPI, dimensions, format, background detection
  const localAnalysis = await extractColorsFromImage(base64);

  // If base64 is SVG but we have the original raster, use that for Vision color analysis
  // This handles the case where PNG was auto-vectorized to SVG before the wizard opened
  const visionTarget = base64.includes('data:image/svg+xml') && originalBase64 && !originalBase64.includes('data:image/svg+xml')
    ? originalBase64
    : base64;

  // SVG with no original raster: local SVG attribute analysis is accurate enough
  if (visionTarget.includes('data:image/svg+xml')) {
    return localAnalysis;
  }

  // For raster images: use Claude Vision for accurate color counting
  try {
    const response = await fetch('/api/logos/analyze-colors', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ base64Data: visionTarget }),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const result = await response.json();
    if (!result.success || !result.data) throw new Error('No data in response');

    const visionData = result.data;
    const dominantColors = (visionData.colors || []).map((c: { hex: string; name: string; percentage: number }) => ({
      hex: c.hex.toLowerCase(),
      count: Math.round((c.percentage / 100) * 1000),
      percentage: c.percentage,
    }));

    console.log(`Vision color analysis: ${visionData.colorCount} colors:`, dominantColors.map((c: { hex: string }) => c.hex));

    return {
      ...localAnalysis,
      colors: dominantColors.map((c: { hex: string }) => c.hex),
      colorCount: visionData.colorCount,
      hasWhiteBackground: visionData.hasWhiteBackground ?? localAnalysis.hasWhiteBackground,
      hasTransparentBackground: visionData.hasTransparency ?? localAnalysis.hasTransparentBackground,
      dominantColors,
    };
  } catch (err) {
    console.warn('Vision color analysis failed, using local analysis:', err);
    return localAnalysis;
  }
};

const LogoWizard: React.FC<LogoWizardProps> = ({
  logoBase64,
  originalBase64,
  techniques,
  language,
  originalFormat,
  onComplete,
  onCancel,
}) => {
  const t = translations[language];

  // Theme color (same as Magic Logotool)
  const THEME_COLOR = '#002B51';

  // Determine steps based on background (skip background step if transparent)
  // Background removal must come BEFORE colors/vectorize so vectorize uses the clean image
  const [steps, setSteps] = useState<WizardStep[]>(['quality', 'background', 'colors', 'technique']);

  // State
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [analysis, setAnalysis] = useState<LogoAnalysis | null>(null);
  const [enabledColors, setEnabledColors] = useState<string[]>([]);
  const [pmsOverrides, setPmsOverrides] = useState<Record<string, string>>({}); // hex -> custom PMS name
  const [currentLogo, setCurrentLogo] = useState(logoBase64);
  const [backgroundRemoved, setBackgroundRemoved] = useState(false);
  const [vectorizedSvg, setVectorizedSvg] = useState<string | null>(null);
  const [selectedTechniqueCode, setSelectedTechniqueCode] = useState<string>(techniques[0]?.code || '');
  const [displayFormat, setDisplayFormat] = useState<string>(originalFormat || 'unknown');

  // Loading states
  const [isAnalyzing, setIsAnalyzing] = useState(true);
  const [isRemovingBg, setIsRemovingBg] = useState(false);
  const [isVectorizing, setIsVectorizing] = useState(false);
  const [vectorizeProgress, setVectorizeProgress] = useState(0);

  const currentStep = steps[currentStepIndex];

  // Analyze logo on mount
  useEffect(() => {
    const analyze = async () => {
      setIsAnalyzing(true);
      try {
        console.log('Starting color analysis...');
        const result = await analyzeColorsWithVision(logoBase64, originalBase64);
        console.log('Color analysis result:', result);
        setAnalysis(result);
        setEnabledColors(result.dominantColors.map(c => c.hex));

        // Flow: quality -> background (optional) -> colors -> technique
        // Background removal MUST happen before colors step because vectorize uses currentLogo
        if (result.hasTransparentBackground) {
          setSteps(['quality', 'colors', 'technique']);
        } else {
          setSteps(['quality', 'background', 'colors', 'technique']);
        }
      } catch (error) {
        console.error('Error analyzing logo:', error);
      } finally {
        setIsAnalyzing(false);
      }
    };
    analyze();
  }, [logoBase64]);

  // Filter techniques by enabled color count
  const filteredTechniques = techniques.filter(tech => {
    const maxColors = tech.maxColors || 99;
    return maxColors >= enabledColors.length || enabledColors.length === 0;
  });

  // Toggle color
  const toggleColor = useCallback((hex: string) => {
    setEnabledColors(prev =>
      prev.includes(hex) ? prev.filter(c => c !== hex) : [...prev, hex]
    );
  }, []);

  // Remove white background using ImageMagick (better for logos than AI-based rembg)
  const handleRemoveBackground = async () => {
    setIsRemovingBg(true);
    try {
      // Use our custom endpoint that uses ImageMagick -transparent white
      // This is much better for logos than rembg which is designed for photos
      const response = await fetch('/api/logos/remove-white-background', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image: currentLogo, fuzz: 10 }),
      });
      const result = await response.json();
      if (result.success && result.data?.image) {
        console.log('[Wizard] White background removed - new image starts with:', result.data.image?.substring(0, 100));
        setCurrentLogo(result.data.image);
        setBackgroundRemoved(true);

        // Re-analyze colors after background removal so we don't include the old background color
        analyzeColorsWithVision(result.data.image).then(newAnalysis => {
          console.log('[Wizard] Re-analyzed colors after bg removal:', newAnalysis.dominantColors.map(c => c.hex));
          setAnalysis(newAnalysis);
          setEnabledColors(newAnalysis.dominantColors.map(c => c.hex));
        });
      } else {
        console.error('Background removal failed:', result.error);
      }
    } catch (error) {
      console.error('Error removing background:', error);
    } finally {
      setIsRemovingBg(false);
    }
  };

  // Vectorize using the same API as MagicLogoModal - direct, no animations
  const handleVectorize = async () => {
    console.log('[Wizard] handleVectorize - currentLogo starts with:', currentLogo?.substring(0, 100));
    console.log('[Wizard] handleVectorize - backgroundRemoved:', backgroundRemoved);
    setIsVectorizing(true);
    setVectorizeProgress(50);

    try {
      const response = await fetch(`${API_BASE_URL}/api/logos/vectorize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          base64Data: currentLogo,
          threshold: 50,
          turdsize: 2,
          alphamax: 1,
          colorMode: true,
          numberOfColors: enabledColors.length || 16,
        }),
      });

      const result = await response.json();

      if (result.success && result.data?.svg) {
        setVectorizedSvg(result.data.svg);
        setVectorizeProgress(100);
        // Advance to next step immediately
        setIsVectorizing(false);
        goNext();
      } else {
        throw new Error(result.error || 'Vectorization failed');
      }
    } catch (error) {
      console.error('Error vectorizing:', error);
      setIsVectorizing(false);
      setVectorizeProgress(0);
    }
  };

  // Navigation
  const goNext = () => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    }
  };

  const goPrevious = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  };

  // Restart wizard from beginning
  const handleRestart = useCallback(() => {
    setCurrentStepIndex(0);
    setCurrentLogo(logoBase64);
    setBackgroundRemoved(false);
    setVectorizedSvg(null);
    setVectorizeProgress(0);
    setIsVectorizing(false);
    // Re-analyze the original logo
    setIsAnalyzing(true);
    analyzeColorsWithVision(logoBase64, originalBase64).then(result => {
      setAnalysis(result);
      setEnabledColors(result.dominantColors.map(c => c.hex));
      if (result.hasTransparentBackground) {
        // Skip background step if already transparent
        setSteps(['quality', 'colors', 'technique']);
      } else {
        // Background removal before colors/vectorize so vectorize uses clean image
        setSteps(['quality', 'background', 'colors', 'technique']);
      }
      setIsAnalyzing(false);
    });
  }, [logoBase64]);

  const handleFinish = () => {
    const colorCount = enabledColors.length <= 1 ? '1' : String(enabledColors.length);
    const finalLogoSrc = vectorizedSvg || currentLogo;
    console.log('[Wizard] handleFinish - vectorizedSvg exists:', !!vectorizedSvg);
    console.log('[Wizard] handleFinish - currentLogo length:', currentLogo?.length);
    console.log('[Wizard] handleFinish - finalLogoSrc starts with:', finalLogoSrc?.substring(0, 100));
    console.log('[Wizard] handleFinish - backgroundRemoved:', backgroundRemoved);
    console.log('[Wizard] handleFinish - detectedColors:', enabledColors);
    onComplete({
      logoSrc: finalLogoSrc,
      selectedTechnique: selectedTechniqueCode,
      selectedColors: colorCount,
      backgroundRemoved,
      detectedColors: enabledColors, // Pass the actual hex colors for paint menu
    });
  };

  // Step labels
  const stepLabels: Record<WizardStep, string> = {
    quality: t.quality,
    colors: t.colors,
    background: t.background,
    technique: t.technique,
  };

  // Render step content
  const renderStepContent = () => {
    // Show vectorizing overlay when vectorizing (triggered from colors step)
    if (isVectorizing) {
      return renderVectorizeStep();
    }

    switch (currentStep) {
      case 'quality':
        return renderQualityStep();
      case 'colors':
        return renderColorsStep();
      case 'background':
        return renderBackgroundStep();
      case 'technique':
        return renderTechniqueStep();
      default:
        return null;
    }
  };

  // Step 1: Quality
  const renderQualityStep = () => {
    if (isAnalyzing) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '32px', color: THEME_COLOR }}></i>
          <p style={{ marginTop: '16px' }}>{t.analyzing}</p>
        </div>
      );
    }

    if (!analysis) return <div>Error analyzing logo</div>;

    // Show original format (eps/ai) if available, otherwise detected format
    const showFormat = originalFormat || analysis.format;

    return (
      <div>
        <h4 style={{ marginBottom: '16px', color: THEME_COLOR }}>{t.qualityTitle}</h4>
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <img
            src={currentLogo}
            alt="Logo"
            style={{ maxWidth: '180px', maxHeight: '120px', border: '1px solid #ddd', borderRadius: '8px' }}
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
          <div style={{ background: '#f8f9fa', padding: '12px', borderRadius: '8px' }}>
            <strong>{t.format}:</strong> {showFormat.toUpperCase()}
          </div>
          <div style={{ background: '#f8f9fa', padding: '12px', borderRadius: '8px' }}>
            <strong>{t.dimensions}:</strong> {analysis.width} x {analysis.height}px
          </div>
        </div>
        <div style={{
          padding: '16px',
          borderRadius: '8px',
          background: analysis.dpiWarning ? '#fff3cd' : '#d4edda',
          border: `1px solid ${analysis.dpiWarning ? '#ffc107' : '#28a745'}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{
              background: analysis.dpiWarning ? '#ffc107' : '#28a745',
              color: '#fff',
              padding: '8px 16px',
              borderRadius: '6px',
              fontWeight: 'bold',
              fontSize: '18px',
            }}>
              {analysis.dpi} DPI
            </span>
            <span>
              <i className={`fas ${analysis.dpiWarning ? 'fa-exclamation-triangle' : 'fa-check-circle'}`}
                style={{ marginRight: '8px', color: analysis.dpiWarning ? '#856404' : '#155724' }}></i>
              {analysis.dpiWarning ? t.qualityWarning : t.qualityOk}
            </span>
          </div>
        </div>
      </div>
    );
  };

  // Step 2: Colors
  // Update PMS override for a color
  const updatePmsOverride = (hex: string, pmsValue: string) => {
    setPmsOverrides(prev => ({ ...prev, [hex]: pmsValue }));
  };

  // Get PMS name (override or auto-detected)
  const getPmsName = (hex: string): string => {
    if (pmsOverrides[hex]) return pmsOverrides[hex];
    return findClosestPMS(hex);
  };

  const renderColorsStep = () => {
    if (!analysis) return null;

    return (
      <div>
        <h4 style={{ marginBottom: '8px', color: THEME_COLOR }}>{t.colorsTitle}</h4>
        <p style={{ color: '#666', marginBottom: '8px' }}>{analysis.colorCount} {t.colorsDetected}</p>
        <p style={{ fontSize: '13px', color: '#888', marginBottom: '16px' }}>{t.colorsToggle}</p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px', maxHeight: '200px', overflowY: 'auto' }}>
          {analysis.dominantColors.map((color, idx) => {
            const isEnabled = enabledColors.includes(color.hex);
            const pmsName = getPmsName(color.hex);
            return (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '8px',
                  background: isEnabled ? '#f0f7ff' : '#f5f5f5',
                  borderRadius: '8px',
                  border: isEnabled ? `2px solid ${THEME_COLOR}` : '1px solid #ddd',
                  opacity: isEnabled ? 1 : 0.6,
                }}
              >
                {/* Color swatch - click to toggle */}
                <div
                  onClick={() => toggleColor(color.hex)}
                  style={{
                    width: '40px',
                    height: '40px',
                    backgroundColor: color.hex,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    position: 'relative',
                    flexShrink: 0,
                    border: '1px solid #ccc',
                  }}
                  title="Klik om aan/uit te zetten"
                >
                  {!isEnabled && (
                    <i className="fas fa-times" style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      color: '#fff',
                      textShadow: '0 0 3px #000',
                      fontSize: '16px',
                    }}></i>
                  )}
                </div>

                {/* HEX code */}
                <span style={{ fontSize: '12px', color: '#666', width: '70px', fontFamily: 'monospace' }}>
                  {color.hex.toUpperCase()}
                </span>

                {/* PMS input field */}
                <input
                  type="text"
                  value={pmsOverrides[color.hex] ?? pmsName}
                  onChange={(e) => updatePmsOverride(color.hex, e.target.value)}
                  placeholder="PMS..."
                  style={{
                    flex: 1,
                    padding: '6px 10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '12px',
                    minWidth: '100px',
                  }}
                  onClick={(e) => e.stopPropagation()}
                />

                {/* Percentage */}
                <span style={{ fontSize: '11px', color: '#999', width: '40px', textAlign: 'right' }}>
                  {color.percentage.toFixed(0)}%
                </span>
              </div>
            );
          })}
        </div>

        <div style={{ background: '#e8f4fc', padding: '12px', borderRadius: '8px', border: `1px solid ${THEME_COLOR}` }}>
          <i className="fas fa-info-circle" style={{ marginRight: '8px', color: THEME_COLOR }}></i>
          {t.colorsEnabled}: <strong>{enabledColors.length}</strong>
        </div>
      </div>
    );
  };

  // Step 3: Background (optional - only shown if not transparent)
  const renderBackgroundStep = () => {
    if (!analysis) return null;

    let bgStatus = t.backgroundSolid;
    let bgIcon = 'fa-circle';
    let bgColor = '#ffc107';

    if (analysis.hasTransparentBackground) {
      bgStatus = t.backgroundTransparent;
      bgIcon = 'fa-check-circle';
      bgColor = '#28a745';
    } else if (analysis.hasWhiteBackground) {
      bgStatus = t.backgroundWhite;
      bgIcon = 'fa-circle';
      bgColor = '#6c757d';
    }

    return (
      <div>
        <h4 style={{ marginBottom: '10px' }}>{t.backgroundTitle}</h4>

        <div style={{
          background: 'repeating-conic-gradient(#ccc 0% 25%, #fff 0% 50%) 50% / 20px 20px',
          padding: '12px',
          borderRadius: '8px',
          textAlign: 'center',
          marginBottom: '10px',
        }}>
          <img src={currentLogo} alt="Logo" style={{ maxWidth: '140px', maxHeight: '90px' }} />
        </div>

        <div style={{ background: '#f8f9fa', padding: '8px 12px', borderRadius: '8px', marginBottom: '10px', fontSize: '14px' }}>
          <i className={`fas ${bgIcon}`} style={{ marginRight: '8px', color: bgColor }}></i>
          {bgStatus}
        </div>

        {!backgroundRemoved && (
          <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
            <button
              onClick={handleRemoveBackground}
              disabled={isRemovingBg}
              style={{
                width: '100%',
                padding: '16px 20px',
                background: THEME_COLOR,
                color: '#fff',
                border: 'none',
                borderRadius: '10px',
                cursor: isRemovingBg ? 'wait' : 'pointer',
                fontSize: '16px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                minHeight: '54px',
                boxShadow: '0 4px 12px rgba(214, 46, 110, 0.3)',
                transition: 'transform 0.2s, box-shadow 0.2s',
              }}
              onMouseOver={(e) => { if (!isRemovingBg) { e.currentTarget.style.transform = 'scale(1.02)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(214, 46, 110, 0.4)'; }}}
              onMouseOut={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(214, 46, 110, 0.3)'; }}
            >
              {isRemovingBg ? (
                <><i className="fas fa-spinner fa-spin" style={{ fontSize: '18px' }}></i><span>{t.backgroundRemoving}</span></>
              ) : (
                <><i className="fas fa-eraser" style={{ fontSize: '18px' }}></i><span>{t.backgroundRemove}</span></>
              )}
            </button>
            <button
              onClick={goNext}
              style={{
                width: '100%',
                padding: '14px 20px',
                background: '#f8f9fa',
                color: '#495057',
                border: '2px solid #dee2e6',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '15px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                minHeight: '48px',
                transition: 'background 0.2s, border-color 0.2s',
              }}
              onMouseOver={(e) => { e.currentTarget.style.background = '#e9ecef'; e.currentTarget.style.borderColor = '#adb5bd'; }}
              onMouseOut={(e) => { e.currentTarget.style.background = '#f8f9fa'; e.currentTarget.style.borderColor = '#dee2e6'; }}
            >
              <i className="fas fa-forward" style={{ fontSize: '15px' }}></i>
              <span>{t.backgroundKeep}</span>
            </button>
          </div>
        )}

        {backgroundRemoved && (
          <div style={{ background: '#d4edda', padding: '12px', borderRadius: '8px', color: '#155724' }}>
            <i className="fas fa-check-circle" style={{ marginRight: '8px' }}></i>
            {t.backgroundRemoved}
          </div>
        )}
      </div>
    );
  };

  // Step 4: Vectorize
  const renderVectorizeStep = () => {
    // Show simple progress while vectorizing
    if (isVectorizing) {
      return (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          minHeight: '280px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '48px', color: THEME_COLOR, marginBottom: '24px' }}></i>
          <h3 style={{
            color: THEME_COLOR,
            marginBottom: '20px',
            fontWeight: 500,
            fontSize: '18px',
          }}>
            {t.vectorizing}
          </h3>
          <div style={{
            background: '#e0e0e0',
            borderRadius: '20px',
            height: '12px',
            overflow: 'hidden',
            width: '100%',
            maxWidth: '300px',
          }}>
            <div style={{
              background: THEME_COLOR,
              height: '100%',
              width: `${vectorizeProgress}%`,
              borderRadius: '20px',
              transition: 'width 0.3s ease',
            }} />
          </div>
        </div>
      );
    }

    // Show success state if already vectorized
    if (vectorizedSvg) {
      return (
        <div>
          <h4 style={{ marginBottom: '16px' }}>{t.vectorizeTitle}</h4>
          <div style={{
            background: '#f8f9fa',
            padding: '20px',
            borderRadius: '8px',
            textAlign: 'center',
            marginBottom: '16px',
          }}>
            <img src={vectorizedSvg} alt="Vectorized" style={{ maxWidth: '180px', maxHeight: '120px' }} />
          </div>
          <div style={{ background: '#d4edda', padding: '12px', borderRadius: '8px', color: '#155724' }}>
            <i className="fas fa-check-circle" style={{ marginRight: '8px' }}></i>
            {t.vectorizeSuccess}
          </div>
        </div>
      );
    }

    // Initial state - prompt to vectorize
    return (
      <div>
        <h4 style={{ marginBottom: '16px' }}>{t.vectorizeTitle}</h4>

        <div style={{
          background: '#f8f9fa',
          padding: '20px',
          borderRadius: '8px',
          textAlign: 'center',
          marginBottom: '16px',
        }}>
          <img src={currentLogo} alt="Logo" style={{ maxWidth: '180px', maxHeight: '120px' }} />
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
          {enabledColors.map((hex, idx) => (
            <div key={idx} style={{
              width: '28px',
              height: '28px',
              backgroundColor: hex,
              border: '1px solid #ccc',
              borderRadius: '4px',
            }} title={hex}></div>
          ))}
        </div>

        <button
          onClick={handleVectorize}
          style={{
            width: '100%',
            padding: '16px',
            background: `linear-gradient(135deg, ${THEME_COLOR} 0%, #003d73 100%)`,
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
          }}
        >
          <i className="fas fa-magic" style={{ marginRight: '8px' }}></i>
          {t.vectorizeTitle}
        </button>
      </div>
    );
  };

  // Step 5: Technique
  const renderTechniqueStep = () => {
    return (
      <div>
        <h4 style={{ marginBottom: '8px' }}>{t.techniqueTitle}</h4>
        <div style={{ background: '#e8f4fc', padding: '10px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px' }}>
          <i className="fas fa-filter" style={{ marginRight: '8px', color: THEME_COLOR }}></i>
          {t.techniqueFilter}: <strong>{enabledColors.length}</strong> {t.colors.toLowerCase()}
        </div>

        {filteredTechniques.length === 0 ? (
          <div style={{ background: '#fff3cd', padding: '12px', borderRadius: '8px', color: '#856404' }}>
            {t.noTechniques}
          </div>
        ) : (
          <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
            {filteredTechniques.map(tech => {
              const isSelected = selectedTechniqueCode === tech.code;
              return (
                <div
                  key={tech.code}
                  onClick={() => setSelectedTechniqueCode(tech.code)}
                  style={{
                    padding: '12px',
                    marginBottom: '8px',
                    border: isSelected ? `2px solid ${THEME_COLOR}` : '1px solid #ddd',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    background: isSelected ? '#e8f4fc' : '#fff',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <strong>{tech.code}</strong>
                      <span style={{ marginLeft: '8px', color: '#666' }}>{tech.name}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ background: '#6c757d', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>
                        {t.maxColors}: {tech.maxColors}
                      </span>
                      {tech.dimensions && (
                        <span style={{ background: '#f8f9fa', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>
                          {tech.dimensions}
                        </span>
                      )}
                      {isSelected && <i className="fas fa-check-circle" style={{ color: THEME_COLOR }}></i>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Check if we can go to next
  const canGoNext = () => {
    return true;
  };

  // Handle next button click - on colors step, trigger vectorization first (unless already SVG)
  const handleNextClick = () => {
    if (currentStep === 'colors') {
      // If already SVG format (e.g., from converted AI/EPS), skip vectorization
      const isAlreadySvg = analysis?.format === 'svg' || currentLogo.includes('data:image/svg+xml');
      if (isAlreadySvg) {
        console.log('[Wizard] Input is already SVG, skipping vectorization');
        setVectorizedSvg(currentLogo); // Use current logo as the "vectorized" result
        goNext();
      } else {
        // Start vectorization, which will auto-advance after completion
        handleVectorize();
      }
    } else {
      goNext();
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
    }}>
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
      `}</style>
      <div style={{
        background: '#fff',
        borderRadius: '8px',
        width: '95%',
        maxWidth: '1000px',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
      }}>
        {/* Header */}
        <div style={{
          padding: '12px 20px',
          borderBottom: '1px solid #eee',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: THEME_COLOR,
          color: '#fff',
        }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <i className="fas fa-hat-wizard" style={{ fontSize: '16px' }}></i>
            {t.wizardTitle}
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              onClick={handleRestart}
              title={t.restart}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                fontSize: '13px',
                cursor: 'pointer',
                color: '#fff',
                padding: '6px 12px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <i className="fas fa-redo"></i>
              {t.restart}
            </button>
            <button onClick={onCancel} style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              fontSize: '18px',
              cursor: 'pointer',
              color: '#fff',
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        {/* Breadcrumb */}
        <div style={{ padding: '16px 20px', background: '#f8f9fa', borderBottom: '1px solid #eee' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            {steps.map((step, idx) => {
              const isActive = idx === currentStepIndex;
              const isCompleted = idx < currentStepIndex;
              return (
                <React.Fragment key={step}>
                  <div
                    onClick={() => isCompleted && setCurrentStepIndex(idx)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      cursor: isCompleted ? 'pointer' : 'default',
                    }}
                  >
                    <span style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: isActive ? THEME_COLOR : isCompleted ? '#28a745' : '#dee2e6',
                      color: isActive || isCompleted ? '#fff' : '#6c757d',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '13px',
                    }}>
                      {isCompleted ? <i className="fas fa-check" style={{ fontSize: '11px' }}></i> : idx + 1}
                    </span>
                    <span style={{
                      fontWeight: isActive ? '600' : '400',
                      color: isActive ? THEME_COLOR : isCompleted ? '#28a745' : '#6c757d',
                      fontSize: '13px',
                    }}>
                      {stepLabels[step]}
                    </span>
                  </div>
                  {idx < steps.length - 1 && (
                    <i className="fas fa-chevron-right" style={{ color: isCompleted ? '#28a745' : '#dee2e6', fontSize: '12px' }}></i>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '20px', flex: 1, overflowY: 'hidden', minHeight: '320px', maxHeight: '320px' }}>
          {renderStepContent()}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 20px',
          borderTop: '1px solid #eee',
          display: 'flex',
          justifyContent: 'space-between',
        }}>
          <button
            onClick={currentStepIndex === 0 ? onCancel : goPrevious}
            style={{
              padding: '10px 20px',
              background: '#6c757d',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            {currentStepIndex === 0 ? (
              <><i className="fas fa-times" style={{ marginRight: '6px' }}></i>{t.cancel}</>
            ) : (
              <><i className="fas fa-chevron-left" style={{ marginRight: '6px' }}></i>{t.previous}</>
            )}
          </button>

          {currentStepIndex === steps.length - 1 ? (
            <button
              onClick={handleFinish}
              style={{
                padding: '10px 24px',
                background: `linear-gradient(135deg, ${THEME_COLOR} 0%, #003d73 100%)`,
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              <i className="fas fa-check" style={{ marginRight: '6px' }}></i>{t.finish}
            </button>
          ) : (
            <button
              onClick={handleNextClick}
              disabled={!canGoNext() || isVectorizing}
              style={{
                padding: '10px 24px',
                background: canGoNext() && !isVectorizing ? (currentStep === 'colors' ? `linear-gradient(135deg, ${THEME_COLOR} 0%, #003d73 100%)` : THEME_COLOR) : '#ccc',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: canGoNext() && !isVectorizing ? 'pointer' : 'not-allowed',
                fontSize: '14px',
              }}
            >
              {currentStep === 'colors' ? (
                // Show "Next" instead of "Vectorize" if already SVG
                (analysis?.format === 'svg' || currentLogo.includes('data:image/svg+xml')) ? (
                  <>{t.next}<i className="fas fa-chevron-right" style={{ marginLeft: '6px' }}></i></>
                ) : (
                  <><i className="fas fa-magic" style={{ marginRight: '6px' }}></i>{t.vectorize}</>
                )
              ) : (
                <>{t.next}<i className="fas fa-chevron-right" style={{ marginLeft: '6px' }}></i></>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LogoWizard;
